import React from 'react';
import PropTypes from 'prop-types';
import { Portal } from 'react-portal';
import Icon from 'relements/build/esm/ui/icon';
import Search from 'relements/build/esm/input/search';

import { search } from '@src/api';

import { Switch, Case } from './components/SwitchCase';
import FilterDrawer from './components/FilterDrawer';
import Header from './components/Header';
import ZeroState from './components/ZeroState';
import Loading from './components/Loading';
import NoResults from './components/NoResults';
import Results from './components/Results';

import styles from './FindReplace.scss';
import useActivify from './hooks/useActivify';

import { STATUS, DEFAULT_FILTERS } from './const';
import { normalizeFilters, normalizeResponse } from './utils/normalizers';

function FindReplace({ onClose, active, domain }) {
  const [status, setStatus] = React.useState(STATUS.ZERO_STATE);
  const [filters, setFilters] = React.useState(DEFAULT_FILTERS);
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  const [filtersChanged, setFiltersChanged] = React.useState(false);
  const [matchCaseEnabled, setMatchCaseEnabled] = React.useState(false);
  const [regexEnabled, setRegexEnabled] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [currentSearchTerm, setCurrentSearchTerm] = React.useState('');
  const [searchResults, setSearchResults] = React.useState([]);

  let numFilters = 0;
  Object.keys(DEFAULT_FILTERS).forEach(key => {
    const difference = DEFAULT_FILTERS[key].length - filters[key].length;
    if (difference > 0) numFilters += 1;
  });

  const handleSearch = React.useCallback(
    async (text = searchTerm, searchFilters = filters) => {
      setCurrentSearchTerm(searchTerm);

      if (!text.length) return setStatus(STATUS.ZERO_STATE);
      const normalizedFilters = normalizeFilters(searchFilters);
      const params = {
        ...normalizedFilters,
        ignore_case: !matchCaseEnabled,
        regex: regexEnabled,
        term: text,
        domain: domain.title,
      };

      setFiltersChanged(false);
      setFiltersOpen(false);
      setStatus(STATUS.LOADING);

      const response = await search(params);
      const results = normalizeResponse(response.body);
      setSearchResults(results);

      if (results.length) setStatus(STATUS.RESULTS);
      else setStatus(STATUS.NO_RESULTS);

      return true;
    }
  );

  const handleResultClick = React.useCallback((id, type) => {
    const GENERAL_RESPONSES_URL = `#/bot/${domain.id}/general-responses`;
    const REDIRECTOR_URL = `#/search/nodes/${id}?type=${type}`;
    window.open(
      type === 'generic_responses' ? GENERAL_RESPONSES_URL : REDIRECTOR_URL,
      '_blank'
    );
  });

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setFiltersChanged(false);
    handleSearch(searchTerm, DEFAULT_FILTERS);
  };

  React.useEffect(() => {
    handleSearch(searchTerm);
  }, [regexEnabled, matchCaseEnabled]);

  React.useEffect(() => {
    if (filters === DEFAULT_FILTERS) return;
    setFiltersChanged(true);
  }, [filters]);

  const { visible, enabled } = useActivify(active);
  const activeClassName = visible ? styles.active : '';

  if (!enabled) return null;

  return (
    <Portal
      node={document && document.getElementById('app-body')}
      active={visible}
    >
      <div
        id="find-replace"
        className={`${styles.findReplace} ${activeClassName}`}
      >
        <div className={styles.findReplaceHeader}>
          <div>
            {domain.title} <span>→ Search</span>
          </div>
          <Search
            className={styles.findReplaceSearch}
            value={searchTerm}
            onType={term => setSearchTerm(term)}
            onSubmit={() => handleSearch()}
            placeholder="Type to search"
            hint="⏎ Press enter to search"
            autoFocus
          />
          <Icon onClick={onClose} src="close" />
        </div>
        <FilterDrawer
          active={filtersOpen}
          value={filters}
          onChange={setFilters}
          onClear={clearFilters}
          filtersChanged={filtersChanged}
          onApply={() => handleSearch(searchTerm)}
          onClose={() => setFiltersOpen(!filtersOpen)}
        />
        <div className={styles.findReplaceBody}>
          <Header
            status={status}
            searchTerm={currentSearchTerm}
            searchResults={searchResults}
            filtersOpen={filtersOpen}
            numFilters={numFilters}
            onFilterClick={() => setFiltersOpen(!filtersOpen)}
            matchCase={matchCaseEnabled}
            regex={regexEnabled}
            onMatchCaseClick={setMatchCaseEnabled}
            onRegexClick={setRegexEnabled}
          />
          <Switch value={status}>
            <Case value={STATUS.ZERO_STATE}>
              <ZeroState />
            </Case>
            <Case value={STATUS.LOADING}>
              <Loading />
            </Case>
            <Case value={STATUS.NO_RESULTS}>
              <NoResults />
            </Case>
            <Case value={STATUS.RESULTS}>
              <Results
                searchResults={searchResults}
                onResultClick={handleResultClick}
              />
            </Case>
          </Switch>
        </div>
      </div>
    </Portal>
  );
}

FindReplace.propTypes = {
  active: PropTypes.bool,
  onClose: PropTypes.func,
  domain: PropTypes.shape({
    title: PropTypes.string,
  }),
};

FindReplace.defaultProps = {
  active: false,
  onClose: () => {},
  domain: {},
};

export default FindReplace;
